import { useId } from 'react'
import { useNavigate } from 'react-router-dom'
import * as Tooltip from '@radix-ui/react-tooltip'
import { BarChart3, Headphones, LayoutDashboard, Lock, MessageSquare } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'

export type ReportsTab = 'delivery' | 'scorecard' | 'bau' | 'surveys'

const TAB_BASE = 'px-4 py-2 text-sm font-semibold border-b-2 inline-flex items-center gap-1.5'

const TABS: { id: ReportsTab; path: string; label: string; icon: LucideIcon; surveyResults?: boolean }[] = [
  { id: 'delivery', path: '/reports', label: 'Delivery Performance', icon: BarChart3 },
  { id: 'scorecard', path: '/scorecard', label: 'Scorecard', icon: LayoutDashboard },
  { id: 'bau', path: '/bau', label: 'Operational BAU', icon: Headphones },
  // Survey answers are about IT itself, so only the Survey results role sees them.
  { id: 'surveys', path: '/survey-results', label: 'Surveys', icon: MessageSquare, surveyResults: true },
]

// Everyone `canReadSurveyResults` lets in: platform and survey admins hold the
// Survey results role through their own.
const RESTRICTED_NOTE = 'Only survey admins, platform admins and people with the Survey results role can see this tab.'

/**
 * Tab strip shared by the report pages: they are one Reports entry in the
 * sidebar, the way the Settings pages are one Settings entry. Each page keeps
 * its own URL, title and controls; this strip is presentation only.
 */
export default function ReportsTabs({ active }: { active: ReportsTab }) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const noteId = useId()
  const tabs = TABS.filter(tab => !tab.surveyResults || user?.canReadSurveyResults)
  return (
    <Tooltip.Provider delayDuration={150} skipDelayDuration={100}>
      <div className="border-b border-slate-200 mb-6">
        <nav className="flex gap-1" aria-label="Reports">
          {tabs.map(({ id, path, label, icon: Icon, surveyResults }) => {
            const tab = (
              <button
                key={id}
                type="button"
                aria-current={id === active ? 'page' : undefined}
                aria-disabled={id === active || undefined}
                aria-describedby={surveyResults ? noteId : undefined}
                onClick={id === active ? undefined : () => navigate(path)}
                className={id === active
                  ? `${TAB_BASE} border-danone-cyan text-slate-800`
                  : `${TAB_BASE} border-transparent text-slate-500 hover:text-slate-700`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
                {/* A quiet reminder that most people do not see this tab. */}
                {surveyResults && <Lock className="w-3 h-3 text-slate-400" aria-hidden />}
              </button>
            )
            if (!surveyResults) return tab
            // The tooltip hangs off the tab itself, so focus shows it as well as
            // hover, and the note stays on the tab for screen readers either way.
            return (
              <Tooltip.Root key={id}>
                <Tooltip.Trigger asChild>{tab}</Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content side="bottom" sideOffset={6}
                                   className="z-[70] max-w-xs rounded-lg bg-slate-900 px-3 py-2 text-xs text-white shadow-xl">
                    {RESTRICTED_NOTE}
                    <Tooltip.Arrow className="fill-slate-900" />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            )
          })}
        </nav>
        {tabs.some(tab => tab.surveyResults) && <span id={noteId} className="sr-only">{RESTRICTED_NOTE}</span>}
      </div>
    </Tooltip.Provider>
  )
}
